import { motion } from 'framer-motion';
import { ArrowUpRight, Coffee, Leaf } from 'lucide-react';
import { products } from '@/data/products';
import { SHOP } from '@/constants/testIds';

export default function ShopFavourites() {
  return (
    <section
      id="shop"
      data-testid={SHOP.root}
      className="relative py-24 md:py-32 overflow-hidden"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.24em] text-[#2D8B5B] mb-4">Shop Favourites</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1D7448] font-light leading-[1.05] max-w-2xl">
              Three rituals. <span className="italic">One for every part of your day.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="max-w-sm text-[15px] text-[#4A6756] leading-relaxed"
          >
            Each blend is small-batch, whole-leaf, and steeped in intention.
            Pour, breathe, and let the herbs meet you where you are.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {products.map((p, i) => (
            <motion.article
              key={p.slug}
              data-testid={SHOP.card(p.slug)}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              className="group relative flex flex-col rounded-[28px] bg-[#FDFBF7] border border-[#E5DCC3] overflow-hidden transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_30px_60px_-30px_rgba(13,58,36,0.35)]"
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                />
                <div className="absolute top-4 left-4 flex items-center gap-2">
                  <span className="text-[11px] uppercase tracking-[0.2em] bg-[#FAF4E3]/90 backdrop-blur text-[#1D7448] px-3 py-1.5 rounded-full border border-[#E5DCC3]">
                    {p.caffeine ? (
                      <span className="inline-flex items-center gap-1"><Coffee className="w-3 h-3" /> Caffeine</span>
                    ) : (
                      <span className="inline-flex items-center gap-1"><Leaf className="w-3 h-3" /> Caffeine free</span>
                    )}
                  </span>
                </div>
              </div>

              <div className="p-7 md:p-8 flex flex-col gap-5 flex-1">
                <div>
                  <h3 className="font-serif text-3xl text-[#1D7448] leading-tight">{p.name}</h3>
                  <p className="text-sm text-[#4A6756] mt-1 italic">{p.tagline}</p>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  {p.benefits.map((b) => (
                    <span
                      key={b}
                      className="text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-[#F3EAD3] text-[#1D7448]"
                    >
                      {b}
                    </span>
                  ))}
                </div>

                <p className="text-sm text-[#4A6756] leading-relaxed">
                  <span className="text-[#1D7448] font-medium">Blended with </span>
                  {p.ingredients.slice(0, 4).join(' · ')}
                  {p.ingredients.length > 4 && ' · …'}
                </p>

                <button
                  data-testid={SHOP.cta(p.slug)}
                  className="mt-auto self-start inline-flex items-center gap-2 text-[#1D7448] font-semibold text-sm group/btn"
                >
                  <span className="link-sweep">View product</span>
                  <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1 group-hover/btn:-translate-y-1" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
